import formatUrl from '../../../../util/make-url';
import { Box } from 'components/box';
import { css } from '@emotion/react';
import Head from 'next/head';

// NOTE: direct usage of the theme is discouraged in most circumstances.
// DO NOT emulate the following without understanding what you're doing.
import { breakpointsPx } from 'themes/onedayonly';

export const mediaBreakpoints = [
  `(max-width: ${breakpointsPx[0] - 1}px)`,
  `(max-width: ${breakpointsPx[1] - 1}px)`,
];

const getUrl = (url: string, width: number) =>
  formatUrl(url, {
    width,
    height: width,
    bg: 'fff',
    fit: 'fill',
  });

const imgAttrs = ({
  url,
  widths,
  sizes,
}: {
  url: string;
  widths: number[];
  sizes: string[];
}) => ({
  src: getUrl(url, Math.max(...widths)), // we want to use the biggest image as the default src
  srcSet: widths.map(width => `${getUrl(url, width)} ${width}w`).join(', '),
  sizes: sizes.join(', '),
});

const ResponsiveImage = ({
  title,
  url,
  widths,
  sizes,
  preload,
  altText,
}: {
  title: string;
  url: string;
  widths: number[];
  sizes: string[];
  preload?: boolean;
  altText?: string;
}) => {
  const attrs = imgAttrs({ url, widths, sizes });

  /**
   * NOTE: imagesizes prop is not in the link element types for react 18 even though it gets passed through correctly.
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/link#attr-imagesizes
   */
  const linkProps = {
    imageSrcSet: attrs.srcSet,
    imageSizes: attrs.sizes,
  };

  return (
    <>
      {preload && (
        <Head>
          <link
            as="image"
            rel="preload"
            href={attrs.srcSet ? undefined : attrs.src}
            {...linkProps}
            key={`responsive-image-${url}-${widths.join('-')}-${sizes.join(
              '-'
            )}`}
          />
        </Head>
      )}

      <img
        aria-label={altText}
        tabIndex={-1}
        title={title}
        alt={title}
        className="image"
        /**
         * the type for img.fetchPriority is broken in this version of NextJS
         * apparently we'd need to upgrade to next@14.2.4 to fix it
         * but it would be a terrible idea to upgrade NextJS right before BF
         * so for now I'm gonna bypass normal prop systems with a terrible hack.
         *
         * @see https://stackoverflow.com/a/78545860
         * @see https://github.com/vercel/next.js/issues/65161
         *
         * NOTE: lighthouse is still reporting that we should add this attribute for the first image
         * even though I can clearly see that it's present during SSR and correctly formatted.
         * I can't tell why it's not picking it up, but I'm going to have to trust that it's working for now.
         *
         * TODO: FE-1244: remove this hack after upgrading NextJS
         */
        {...(preload ? { ['fetchpriority']: 'high' } : {})}
        {...attrs}
        css={css`
          user-drag: none;
          -webkit-user-drag: none;
          user-select: none;
          -moz-user-select: none;
          -webkit-user-select: none;
          -ms-user-select: none;
          display: block;
          max-width: 100%;
          max-height: 100%;
          min-width: 100%;
          opacity: 1;
          transition-duration: 512ms;
          transition-property: opacity, visibility;
          transition-timing-function: ease-out;
          visibility: visible;
        `}
      />

      {/*  Overlay to prevent the user from right-clicking and save the image */}
      <Box
        zIndex={10}
        position="absolute"
        top="0"
        left="0"
        minWidth="100%"
        minHeight="100%"
      />
    </>
  );
};

export default ResponsiveImage;
